import React, { useState, useEffect, useRef } from "react";
const DropdownComponent = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const buttonRefs = useRef([]);

  const toggleDropdown = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  const dropdownData = [
    {
      title: "AI & Machine Learning Risk Analysis",
      image:
        "https://images.unsplash.com/photo-1627398242454-45a1465c2479?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80",
      text: "Exploring the risks and challenges in implementing AI & ML algorithms. This includes bias, data privacy, and the potential for unforeseen outcomes in critical systems. As artificial intelligence continues to evolve at a rapid pace, organizations must carefully consider the ethical implications and potential pitfalls associated with these powerful technologies.",
      additionalText:
        "Recent studies have shown that AI systems can inherit and even amplify biases present in their training data, leading to discriminatory outcomes. Furthermore, the 'black box' nature of some complex algorithms makes it difficult to understand how they arrive at certain decisions, creating transparency issues. Companies implementing AI solutions must establish robust governance frameworks to monitor and mitigate these risks throughout the AI lifecycle.",
      keyPoints: [
        "AI systems can amplify biases in training data, leading to discriminatory outcomes",
        "The 'black box' nature of complex algorithms creates transparency issues",
        "Robust governance frameworks are essential for monitoring AI risks",
        "Ethical considerations must be integrated throughout the AI lifecycle",
      ],
    },
    {
      title: "Optimizing Supply Chain with AI",
      image:
        "https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80",
      text: "AI-powered systems are revolutionizing supply chain management. From predictive analytics to real-time inventory tracking, AI optimizes operations and reduces waste. These intelligent systems can forecast demand with remarkable accuracy, allowing companies to maintain optimal inventory levels and reduce carrying costs.",
      additionalText:
        "Advanced AI solutions can simulate various supply chain scenarios, helping organizations prepare for disruptions and build more resilient operations. By analyzing historical data, weather patterns, geopolitical events, and even social media sentiment, these systems provide early warnings about potential supply chain disruptions. Companies that have implemented AI-driven supply chain solutions report significant reductions in operational costs.",
      keyPoints: [
        "AI enables accurate demand forecasting and optimal inventory management",
        "Simulation of supply chain scenarios helps build resilience to disruptions",
        "Analysis of multiple data sources provides early warning of potential issues",
        "Companies report significant cost reductions with AI-driven supply chains",
      ],
    },
    {
      title: "API Management for Operational Efficiency",
      image:
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80",
      text: "API management plays a crucial role in streamlining workflows. By optimizing API traffic, businesses can improve operational efficiency and scale their infrastructure. Effective API management enables organizations to expose their services and data securely while maintaining control over access, usage policies, and performance metrics.",
      additionalText:
        "Modern API management platforms provide comprehensive analytics that help organizations understand how their APIs are being used, identify performance bottlenecks, and detect potential security threats. These insights enable continuous optimization of API strategies. Additionally, features like rate limiting, caching, and request transformation help ensure that API consumers have a consistent and reliable experience.",
      keyPoints: [
        "API management optimizes traffic flow and improves operational efficiency",
        "Comprehensive analytics help identify performance bottlenecks and security threats",
        "Rate limiting and caching ensure consistent API consumer experience",
        "Secure exposure of services while maintaining access control is critical",
      ],
    },
    {
      title: "Innovating with Mobile App Development",
      image:
        "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80",
      text: "Mobile app development remains at the forefront of innovation, with custom apps driving user engagement and providing businesses with new avenues for customer interaction. The shift toward mobile-first strategies has forced organizations to rethink their digital presence and prioritize seamless mobile experiences.",
      additionalText:
        "Progressive Web Apps (PWAs) have emerged as a powerful alternative to native applications, offering the best of both web and mobile app experiences. These applications can work offline, send push notifications, and be installed on a user's home screen without going through an app store. Meanwhile, cross-platform development frameworks have reduced development time and costs.",
      keyPoints: [
        "Mobile-first strategies require rethinking digital presence and user experience",
        "Progressive Web Apps offer offline functionality and app-like experiences",
        "Cross-platform development frameworks reduce time and costs",
        "Custom mobile apps drive user engagement and customer interaction",
      ],
    },
    {
      title: "Automating Web App Testing for Efficiency",
      image:
        "https://images.unsplash.com/photo-1432888622747-4eb9a8f5a07a?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80",
      text: "Test automation ensures that web applications perform at their peak. Automated testing speeds up deployment cycles and reduces human error, ensuring reliability. By implementing comprehensive test automation strategies, organizations can significantly improve software quality while reducing time-to-market for new features and applications.",
      additionalText:
        "Modern test automation frameworks support a wide range of testing types including unit testing, integration testing, end-to-end testing, and performance testing. Continuous integration and continuous deployment (CI/CD) pipelines integrate automated testing to provide rapid feedback on code changes, enabling developers to identify and fix issues early in the development lifecycle.",
      keyPoints: [
        "Automated testing accelerates deployment cycles and reduces human error",
        "Comprehensive test automation improves software quality and reduces time-to-market",
        "CI/CD pipelines integrate testing for rapid feedback on code changes",
        "Multiple testing types (unit, integration, e2e, performance) are essential",
      ],
    },
    {
      title: "Cloud-Based Mobile Solutions",
      image:
        "https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80",
      text: "Cloud-based mobile applications provide flexibility, scalability, and cost efficiency. They allow seamless integration across devices and platforms, empowering businesses with agile solutions. By leveraging cloud infrastructure, mobile apps can deliver consistent performance regardless of user location or device capabilities.",
      additionalText:
        "Serverless architectures and backend-as-a-service (BaaS) platforms have further simplified cloud-based mobile development, allowing developers to focus on frontend functionality while relying on scalable cloud services for backend operations. These approaches reduce operational overhead and enable rapid iteration based on user feedback and changing market conditions.",
      keyPoints: [
        "Cloud-based apps provide flexibility, scalability, and cost efficiency",
        "Seamless integration across devices and platforms enables agile solutions",
        "Serverless and BaaS architectures simplify development and reduce overhead",
        "Consistent performance regardless of user location or device capabilities",
      ],
    },
    {
      title: "User-Centric UI/UX Design",
      image:
        "https://images.unsplash.com/photo-1545235617-9465d2a55698?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80",
      text: "UI/UX design focuses on delivering exceptional user experiences. From intuitive interfaces to seamless navigation, a strong UI/UX design drives user engagement and satisfaction. Well-designed interfaces reduce cognitive load, minimize user errors, and create emotional connections that foster brand loyalty.",
      additionalText:
        "The field of UI/UX design continues to evolve with emerging technologies such as voice interfaces, augmented reality, and adaptive interfaces that personalize user experiences based on context and behavior. Design systems and component libraries have become essential tools for maintaining consistency across digital products while enabling efficient collaboration between designers and developers.",
      keyPoints: [
        "Intuitive interfaces and seamless navigation drive user engagement",
        "Reduced cognitive load and minimized errors improve user satisfaction",
        "Emerging technologies (voice, AR, adaptive interfaces) shape UX evolution",
        "Design systems maintain consistency and enable designer-developer collaboration",
      ],
    },
    {
      title: "Scalable Salesforce Solutions",
      image:
        "https://images.unsplash.com/photo-1545235617-9465d2a55698?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80",
      text: "Salesforce solutions optimize sales and CRM processes. By automating workflows and streamlining data management, businesses can enhance scalability and improve customer relationships. The Salesforce platform provides a comprehensive suite of tools for managing customer interactions across marketing, sales, commerce, and service departments.",
      additionalText:
        "With its flexible architecture and extensive ecosystem of third-party applications, Salesforce enables organizations to create tailored solutions that address specific business needs. Advanced features like AI-powered analytics, predictive scoring, and automated workflow orchestration help businesses anticipate customer needs and deliver personalized experiences at scale.",
      keyPoints: [
        "Salesforce automates workflows and streamlines data management for scalability",
        "Comprehensive tools manage customer interactions across departments",
        "Flexible architecture and ecosystem enable tailored business solutions",
        "AI-powered analytics and predictive scoring anticipate customer needs",
      ],
    },
  ];

  const buttonColors = [
    "#e0f2fe", "#fce7f3", "#dcfce7", "#ffedd5", "#fef3c7", "#e0e7ff", "#f3e8ff", "#ffe4e6"
  ];

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setIsVisible(true);
      });
    }, { threshold: 0.1 });

    buttonRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div style={styles.wrapper}>
      <div style={styles.container}>
         <h2 className="page-title"><span style={{ color: '#025261' }}>Case </span> <span style={{ color: 'gold' }}>Studies</span></h2>

        <div style={styles.buttonsContainer}>
          {dropdownData.map((item, index) => (
            <div
              key={index}
              ref={(el) => (buttonRefs.current[index] = el)}
              style={styles.buttonWrapper}
            >
              <button
                style={{
                  ...styles.button,
                  backgroundColor: buttonColors[index % buttonColors.length],
                  ...(activeDropdown === index ? styles.activeButton : {}),
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? "translateY(0)" : "translateY(30px)",
                  transition: "all 0.5s ease-out",
                }}
                onClick={() => toggleDropdown(index)}
              >
                <span>{item.title}</span>
                <span style={styles.arrow}>
                  {activeDropdown === index ? "▲" : "▼"}
                </span>
              </button>

              {activeDropdown === index && (
                <div style={styles.dropdown}>
                  <div style={styles.dropdownContent}>
                    <div className="image-text-wrapper" style={styles.imageTextWrapper}>
                      <img src={item.image} alt={item.title} className="responsive-image" style={styles.image} />
                      <div className="text-content" style={styles.textContent}>
                        <p className="text" style={styles.text}>{item.text}</p>
                        <p className="additional-text" style={styles.additionalText}>{item.additionalText}</p>
                      </div>
                    </div>

                    <div className="key-points" style={styles.keyPoints}>
                      <h3 style={styles.keyPointsTitle}>Key Insights:</h3>
                      <ul className="key-points-list" style={styles.keyPointsList}>
                        {item.keyPoints.map((point, i) => (
                          <li key={i} style={styles.keyPointItem}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const styles = {
  wrapper: {
    width: "100%",
    backgroundColor: "#f8fafc",
    padding: "60px 0",
    fontFamily: "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  container: {
    maxWidth: "1100px",
    margin: "0 auto",
    padding: "0 20px",
  },
  title: {
    textAlign: "justify",
    fontSize: "40px",
    fontWeight: "700",
    color: "#1f2937",
    marginBottom: "20px",
    paddingBottom: "15px",
    letterSpacing:"0px",
  },
  buttonsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  buttonWrapper: {
    marginBottom: "8px",
  },
  button: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    padding: "18px 24px",
    fontSize: "1.2rem",
    fontWeight: "600",
    color: "#374151",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
  },
  activeButton: {
    backgroundColor: "rgba(2, 82, 97, 1)",
    color: "white",
    boxShadow: "0 6px 12px rgba(0, 0, 0, 0.15)",
  },
  arrow: {
    fontSize: "1.2rem",
    fontWeight: "bold",
  },
  dropdown: {
    backgroundColor: "white",
    borderRadius: "0 0 12px 12px",
    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
    marginTop: "2px",
  },
  dropdownContent: {
    padding: "28px",
  },
  imageTextWrapper: {
    display: "flex",
    flexDirection: "row",
    marginBottom: "20px",
    gap: "25px",
    flexWrap: "wrap",
  },
  image: {
    width: "40%",
    height: "250px",
    objectFit: "cover",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    flexShrink: 0,
  },
  textContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  text: {
    fontSize: "1.05rem",
    lineHeight: "1.7",
    color: "black",
    margin: "0 0 15px 0",
    textAlign: "left",
  },
  additionalText: {
    fontSize: "1rem",
    lineHeight: "1.7",
    color: "black",
    margin: "0 0 20px 0",
    textAlign: "left",
  },
  keyPoints: {
    backgroundColor: "#f9fafb",
    padding: "20px",
    borderRadius: "8px",
    borderLeft: "4px solid #4f46e5",
  },
  keyPointsTitle: {
    fontSize: "1.1rem",
    fontWeight: "600",
    color: "#1f2937",
    marginBottom: "12px",
  },
  keyPointsList: {
    margin: "0",
    paddingLeft: "20px",
    columns: "2",
    columnGap: "20px",
  },
  keyPointItem: {
    marginBottom: "8px",
    breakInside: "avoid",
  },
};

// Inject CSS for responsiveness
if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = `
    @media (max-width: 768px) {
      .image-text-wrapper {
        flex-direction: column !important;
      }

      .responsive-image {
        width: 100% !important;
        height: 200px !important;
        margin-bottom: 16px;
      }

      .key-points-list {
        columns: 1 !important;
      }
    }

    @media (max-width: 480px) {
      .text, .additional-text {
        font-size: 0.95rem !important;
      }

      .button {
        padding: 14px 18px !important;
        font-size: 1rem !important;
      }

      .title {
        font-size: 2.2rem !important;
        text-align: center !important;
      }
    }
  `;
  document.head.appendChild(styleElement);
}

export default DropdownComponent;
